/**
 * Video rooms.
 *
 * "Matt also the Google link for each provider can we have a tab to the left
 *  that has the stationed link for each provider to go into when needed. If
 *  it's on the left I also can have access? Incase I need to join or anything"
 *
 * Each provider keeps one Google Meet room, and the client is texted it minutes
 * before a virtual visit. The link already existed - buried in the provider's
 * record under Manage - so this is mostly about putting it one click from
 * wherever she is standing, for her and for the desk.
 *
 * Today's video visits sit at the top, because "when needed" is almost always
 * "somebody is in a room right now and I should be in there too".
 */
import { useCallback, useEffect, useState } from "react";
import { Video, ExternalLink, Copy, Check, Pencil, RefreshCw } from "lucide-react";
import { Button, EmptyState, Input, Skeleton, useToast } from "@/components/ui";
import { Avatar } from "@/components/ui/Avatar";
import { api, ApiError } from "@/lib/api";
import { canAny, useMe } from "@/lib/me";
import { cn } from "@/lib/utils";

interface Provider {
  id: string; name: string; title: string | null; color: string | null;
  meeting_url: string | null; bookable: boolean;
}
interface Visit {
  id: string; starts_at: string; title: string; client: string | null;
  provider_id: string | null; provider_name: string | null; meeting_url: string | null;
}

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export function VideoRoomsPage() {
  const me = useMe();
  const canEdit = canAny(me, ["settings"]);
  const toast = useToast();
  const [data, setData] = useState<{ providers: Provider[]; today: Visit[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ providers: Provider[]; today: Visit[] }>("/providers/rooms")
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => setErr(e instanceof ApiError ? e.message : "That didn't load"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (p: Provider, url: string) => {
    try {
      await api.patch(`/providers/${p.id}`, { meeting_url: url.trim() || null });
      toast.success(url.trim() ? `${p.name.split(" ")[0]}'s room saved` : "Room link cleared");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Couldn't save that");
    }
  };

  return (
    <div data-video-rooms className="space-y-5 p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-zinc-100">
            <Video className="h-5 w-5 text-fuchsia-400" />
            Video rooms
          </h1>
          <p className="mt-1 text-sm text-zinc-400">
            Everyone's standing room, one click away. Clients are texted the link a few minutes
            before their visit.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={load} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>
          Refresh
        </Button>
      </div>

      {err ? <EmptyState icon={Video} title="That didn't load" description={err} />
        : !data ? <Skeleton className="h-40" /> : (
        <>
          {/* Today first: the reason to open this page at all. */}
          {data.today.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-zinc-200">Video visits today</h2>
              <ul className="divide-y divide-zinc-800/70 overflow-hidden rounded-xl border border-zinc-800">
                {data.today.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                    <span className="w-20 shrink-0 text-sm font-medium tabular-nums text-zinc-100">
                      {timeOf(v.starts_at)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-zinc-100">{v.client ?? "Someone"}</span>
                      <span className="block truncate text-xs text-zinc-500">
                        {v.title}
                        {v.provider_name && ` · with ${v.provider_name}`}
                      </span>
                    </span>
                    {v.meeting_url ? (
                      <JoinButton url={v.meeting_url} />
                    ) : (
                      <span className="text-[11px] text-amber-300">
                        {v.provider_name ? `${v.provider_name.split(" ")[0]} has no room link` : "No provider on it"}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-zinc-200">Everyone's room</h2>
            <ul className="divide-y divide-zinc-800/70 overflow-hidden rounded-xl border border-zinc-800">
              {data.providers.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                  <Avatar name={p.name} color={p.color} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-zinc-100">
                      {p.name}
                      {p.title && <span className="ml-1.5 text-zinc-500">{p.title}</span>}
                    </span>
                    {editing === p.id ? (
                      <RoomEditor
                        initial={p.meeting_url ?? ""}
                        onCancel={() => setEditing(null)}
                        onSave={(url) => void save(p, url)}
                      />
                    ) : (
                      <span className="block truncate text-xs text-zinc-500">
                        {p.meeting_url ?? "No room link yet"}
                      </span>
                    )}
                  </span>
                  {editing !== p.id && (
                    <span className="flex shrink-0 items-center gap-1.5">
                      {p.meeting_url && <CopyButton url={p.meeting_url} />}
                      {p.meeting_url && <JoinButton url={p.meeting_url} />}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(p.id)}
                          leftIcon={<Pencil className="h-3.5 w-3.5" />}
                        >
                          {p.meeting_url ? "" : "Add a link"}
                        </Button>
                      )}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-zinc-500">
              The people clients can book with. Anybody who takes no bookings - her medical
              director, for one - does not need a room, and is not listed.
            </p>

            {/*
              "my side said wait for somone to let me in and Christina was this
               … She is not being given the control to let the client in instead
               it says to her to wait on host she is the host"

              Both of them sat in the lobby because Google decides who the host
              is by which Google account made the meeting, not by whose room it
              is here. Two rules, and they are easy to get wrong once and then
              never understand.
            */}
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-xs leading-relaxed text-zinc-300">
              <p className="font-semibold text-amber-200">Two things Google needs, or everybody waits</p>
              <ol className="mt-1.5 list-decimal space-y-1 pl-4">
                <li>
                  Make the room while signed in to the Google account you will always use,
                  and join it signed in to that same account. Google decides who the host is
                  by who made the meeting - sign in as anyone else and it puts you in the
                  queue for your own room.
                </li>
                <li>
                  Turn <strong>Quick access</strong> on for that meeting in Google. Without it
                  the client sits waiting to be let in, which is the whole thing this was
                  meant to avoid.
                </li>
              </ol>
            </div>
            {data.providers.every((p) => !p.meeting_url) && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs leading-relaxed text-zinc-300">
                Nobody has a room link yet. In Google Meet, each person makes one meeting and keeps
                it - "Create a meeting for later" gives a link that works every time. Paste each
                one here and the client is texted it automatically before a video visit.
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function JoinButton({ url }: { url: string }) {
  return (
    <Button
      size="sm"
      onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
      leftIcon={<ExternalLink className="h-3.5 w-3.5" />}
    >
      Join
    </Button>
  );
}

function CopyButton({ url }: { url: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="secondary"
      size="sm"
      aria-label="Copy the link"
      onClick={() => {
        navigator.clipboard.writeText(url).then(
          () => { setDone(true); setTimeout(() => setDone(false), 1600); },
          () => { /* clipboard blocked; the link is on screen */ },
        );
      }}
      leftIcon={done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    >
      {done ? "Copied" : ""}
    </Button>
  );
}

function RoomEditor({
  initial, onSave, onCancel,
}: { initial: string; onSave: (url: string) => void; onCancel: () => void }) {
  const [url, setUrl] = useState(initial);
  return (
    <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <Input
        autoFocus
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSave(url);
          if (e.key === "Escape") onCancel();
        }}
        placeholder="https://meet.google.com/…"
        className={cn("h-8 w-full sm:w-80")}
      />
      <Button size="sm" onClick={() => onSave(url)}>Save</Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
    </span>
  );
}
